import type { ColumnDef } from '@tanstack/react-table';
import { Badge, Text } from '@mantine/core';
import {
  formatDuration,
  formatSize,
  getStatusColor,
} from '~/components/browser/status-panel/panels/NetworkDetailsPanel';
import type { NetworkLog } from '~/common/vm/protocol';

function extractPathFromUrl(url: string) {
  try {
    const urlObj = new URL(url);
    if (urlObj.pathname === '/') {
      return urlObj.hostname;
    }

    const pathSegments = urlObj.pathname.split('/').filter(Boolean);
    return pathSegments.at(-1) ?? urlObj.hostname;
  } catch {
    return url;
  }
}

export const networkColumns = (onNameClick?: (entry: NetworkLog) => void): ColumnDef<NetworkLog>[] => [
  {
    accessorFn: (row) => row.url,
    header: 'Name',
    size: 200,
    cell: ({ row }) => {
      const name = extractPathFromUrl(row.original.url);
      return (
        <Text
          size="sm"
          className="cursor-pointer font-mono text-xs hover:text-blue-500 hover:underline"
          title={name}
          onClick={() => onNameClick?.(row.original)}
        >
          {name}
        </Text>
      );
    },
  },
  {
    accessorFn: (row) => row.method,
    header: 'Method',
    size: 80,
    cell: ({ row }) => (
      <Text
        size="sm"
        className="font-mono text-xs"
      >
        {row.original.method}
      </Text>
    ),
  },
  {
    accessorFn: (row) => row.status?.code,
    header: 'Status',
    size: 80,
    cell: ({ row }) =>
      row.original.status?.code ?
        <Badge
          color={getStatusColor(row.original.status.code)}
          variant="light"
          size="sm"
        >
          {row.original.status.code}
        </Badge>
      : <Text
          size="sm"
          c="dimmed"
        >
          -
        </Text>,
  },
  {
    accessorFn: (row) => row.responseHeaders['content-type'],
    header: 'Content Type',
    size: 150,
    cell: ({ row }) => (
      <Text
        size="sm"
        className="truncate font-mono text-xs"
        title={row.original.responseHeaders['content-type']}
      >
        {row.original.responseHeaders['content-type'] || 'x-unknown'}
      </Text>
    ),
  },
  {
    accessorFn: (row) => row.duration,
    header: 'Duration',
    size: 100,
    cell: ({ row }) => (
      <Text
        size="sm"
        className="font-mono text-xs"
      >
        {formatDuration(row.original.duration)}
      </Text>
    ),
  },
  {
    accessorFn: (row) => row.size,
    header: 'Size',
    size: 80,
    cell: ({ row }) => (
      <Text
        size="sm"
        className="font-mono text-xs"
      >
        {formatSize(row.original.size)}
      </Text>
    ),
  },
  {
    accessorFn: (row) => row.startTime,
    header: 'Start',
    size: 100,
    cell: ({ row }) => (
      <Text
        size="sm"
        className="font-mono text-xs"
      >
        {new Date(row.original.startTime).toLocaleTimeString()}
      </Text>
    ),
  },
];
